import React, { useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import useFetch, {
	Provider as FetchProvider,
	CachePolicies,
	Options,
} from "use-http";

import { ROUTES } from "common";
import {
	ACCESS_TOKEN_COOKIE,
	REFRESH_TOKEN_COOKIE,
} from "common/constants/cookies";
import { getCookieDomain } from "utils/getDomain";
import { setConnectionStatus } from "contexts/connectionStatus";
import Context from "./context";
import { ConfigurableHeader, ConfigurableHeaderName } from "./types";

// Reloading is how a refreshed access token gets picked up, but a reload can
// never fix a 401 the refresh didn't resolve — it just replays it. These two
// bound that: at most one auth reload per cooldown window, then the login page.
const AUTH_RELOAD_KEY = "auth_reload_at";
const AUTH_RELOAD_COOLDOWN_MS = 15000;

const ConfiguredFetchProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const { search, pathname } = useLocation();

	// A visitor in the anonymous Configurator has no session by definition, so
	// any authenticated request reached from there answers 401 — and sending
	// them to the login page throws away the room they have been building,
	// with nothing to log into yet and no way back to it. Their session is
	// never the thing that needs fixing, so the redirects below are suppressed
	// here and the failing request is left to its own caller to handle.
	//
	// This is the backstop, not the fix: a request that 401s here is a request
	// that should not have been made (see the `authenticated` guard in
	// useResolveObjectCategories). It exists so that the next one to slip
	// through costs a failed fetch instead of the visitor's work.
	const inAnonymousConfigurator = pathname.startsWith(
		`/${ROUTES.CONFIGURATOR}`,
	);

	const redirectToLogin = (reason: string, details?: unknown) => {
		if (inAnonymousConfigurator) {
			logger.warn(
				`${reason} - staying put: anonymous Configurator has no session to restore`,
				details,
			);
			return;
		}
		logger.error(`${reason} - redirecting to the Login page`, details);
		history.push({ pathname: `/${ROUTES.LOGIN}`, search });
	};

	const [cookies, setCookie] = useCookies([
		ACCESS_TOKEN_COOKIE,
		REFRESH_TOKEN_COOKIE,
	]);

	// eslint-disable-next-line @typescript-eslint/ban-types
	const [headers, setHeaders] = useState<ConfigurableHeader | {}>({
		Accept: "application/json",
		"Content-Type": "application/json",
	});
	const {
		post: fetchRefreshToken,
		response: refreshTokenResponse,
	} = useFetch(`${process.env.REACT_APP_BASE_URL}/auth/token/refresh`, {
		body: {
			refreshToken: cookies[REFRESH_TOKEN_COOKIE],
		},
	});

	const getHttpOptions = (): Partial<Options> => {
		return {
			cachePolicy: CachePolicies.NO_CACHE,
			headers,
			retryOn: async ({ response }) => {
				// Our 503 comes from requireDatabase, which rejects before any
				// handler runs - so repeating the request cannot duplicate a
				// write. Deliberately narrow: a 502/504 from a proxy can arrive
				// after the server already did the work, and retrying those
				// would risk exactly that. Those cases are handled by the boot
				// gates and the connection banner instead.
				if (response?.status === 503) {
					setConnectionStatus("degraded");
					return true;
				}

				if (response?.status !== 401) {
					return false;
				}

				let message: string | undefined;
				try {
					message = (await response.json())?.message;
				} catch {
					// Error bodies are sometimes empty - parsing one throws.
				}

				// Only an expired access token is worth refreshing for. Every
				// other 401 (token missing/invalid, no x-org, no rights) is a
				// dead end: refreshing wouldn't change it, and the reload below
				// would replay it on every load until the tab is closed.
				if (message !== "msg.token.expired") {
					redirectToLogin("Unrecoverable 401", { message });

					return false;
				}

				// A reload moments ago means the refresh isn't resolving the
				// 401. Reloading again would spin forever.
				const lastReload = Number(
					sessionStorage.getItem(AUTH_RELOAD_KEY) ?? 0,
				);

				if (Date.now() - lastReload < AUTH_RELOAD_COOLDOWN_MS) {
					sessionStorage.removeItem(AUTH_RELOAD_KEY);
					redirectToLogin("Token refresh did not resolve the 401");

					return false;
				}

				logger.info("Auto-refreshing token in silence...");

				await fetchRefreshToken();

				if (!refreshTokenResponse.ok) {
					redirectToLogin(
						"There was an error fetching refresh token",
					);

					return false;
				}

				const newLoginData = await refreshTokenResponse.json();
				setCookie(ACCESS_TOKEN_COOKIE, newLoginData.message.token, {
					path: "/",
					domain: getCookieDomain(),
				});
				setCookie(
					REFRESH_TOKEN_COOKIE,
					newLoginData.message.refreshToken,
					{
						path: "/",
						domain: getCookieDomain(),
					},
				);

				sessionStorage.setItem(AUTH_RELOAD_KEY, String(Date.now()));
				window.location.reload();

				return true;
			},
			retries: 2,
			retryDelay: ({ attempt }) => Math.min(1000 * 2 ** attempt, 8000),
			interceptors: {
				// Attempt to translate response message codes
				response: async ({ response }) => {
					const res: any = response;
					if (res.ok) {
						// The token works - a later 401 gets a fresh reload budget.
						sessionStorage.removeItem(AUTH_RELOAD_KEY);
						// Any successful response proves the backend is back,
						// whichever request happened to prove it.
						setConnectionStatus("online");
					}
					// Only string messages are codes. Some endpoints (POST
					// /auth/login) put an object in `message`, and calling
					// .includes on that throws in here - use-http swallows it
					// and the caller gets `undefined` back from a 200.
					if (
						typeof res.data?.message === "string" &&
						res.data.message.includes(".")
					) {
						res.data.translatedMessage = t(
							res.data.message,
							res.data.message,
						);
					}

					return res;
				},
			},
		};
	};

	const removeHeader = (name: ConfigurableHeaderName) => {
		const newHeaders = { ...headers } as ConfigurableHeader;

		if (newHeaders[name]) {
			delete newHeaders[name];
		}

		setHeaders(newHeaders);
	};

	const setHeader = (name: ConfigurableHeaderName, value: string) => {
		setHeaders({
			...headers,
			[name]: value,
		});
	};

	return (
		<Context.Provider
			value={{
				removeHeader,
				setHeader,
				headers,
			}}
		>
			<FetchProvider
				options={getHttpOptions()}
				url={process.env.REACT_APP_BASE_URL}
			>
				{children}
			</FetchProvider>
		</Context.Provider>
	);
};

export default ConfiguredFetchProvider;
